import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { DropResult } from 'react-beautiful-dnd';

import { StorageE } from 'declarations/enums/storage';
import {
  type WipColumnConfigI,
  reorderWipColumn,
  selectWipColumnOrder,
  setWipColumnOrder,
  toggleWipColumnVisibility,
} from 'reduxStore/features/wip/wipSlice';
import { useAppDispatch, useAppSelector } from 'reduxStore/hooks';

import {
  generateDefaultWipColumnOrder,
  reconcileWipColumnOrder,
} from '../components/WIPTable/WIPItemsTableConfiguration';

/** Persistent storage helpers for the WIP picker's column order. */
const readStoredColumnOrder = (): WipColumnConfigI[] | null => {
  try {
    const raw = localStorage.getItem(StorageE.WipTableColumnOrder);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed
      .filter(
        (c): c is { id: unknown; visible: unknown } =>
          typeof c === 'object' && c !== null && 'id' in c && 'visible' in c
      )
      .map(
        (c): WipColumnConfigI => ({
          id: String(c.id),
          visible: Boolean(c.visible),
          required: false,
        })
      );
  } catch {
    return null;
  }
};

const writeStoredColumnOrder = (order: WipColumnConfigI[]): void => {
  try {
    localStorage.setItem(StorageE.WipTableColumnOrder, JSON.stringify(order));
  } catch {
    // localStorage may be unavailable (private mode, quota); the in-memory Redux copy still works.
  }
};

const clearStoredColumnOrder = (): void => {
  try {
    localStorage.removeItem(StorageE.WipTableColumnOrder);
  } catch {
    // best-effort
  }
};

export interface UseWipColumnPickerI {
  /** The ordered list of columns to render in the picker (always non-empty). */
  columnOrder: WipColumnConfigI[];
  /** Handler for react-beautiful-dnd's `onDragEnd`. */
  handleOrderChange: (result: DropResult) => void;
  /** Toggle a column's visibility; ignored for required columns. */
  handleVisibilityToggle: (columnId: string) => void;
  /** Reset to the configuration defaults (clears persisted order). */
  handleResetConfig: () => void;
  /** Persist the current column order (also called eagerly on every interaction). */
  handleSaveConfig: () => void;
}

/**
 * Owns the WIP "Customize Columns" state. Mirrors WarrQueue's `useWIPColumnPicker` contract:
 *  - hydrates from localStorage on mount (reconciled against the current configuration),
 *  - applies user edits to Redux immediately (so the table reacts instantly),
 *  - persists every change to localStorage so the order survives reloads,
 *  - exposes Reset (revert to defaults) and Save (idempotent persist + close) handlers.
 */
export const useWipColumnPicker = (): UseWipColumnPickerI => {
  const dispatch = useAppDispatch();
  const reduxOrder = useAppSelector(selectWipColumnOrder);
  const hasHydratedRef = useRef<boolean>(false);

  useEffect((): void => {
    if (hasHydratedRef.current) return;
    hasHydratedRef.current = true;
    const stored = readStoredColumnOrder();
    const hydrated = reconcileWipColumnOrder(stored ?? generateDefaultWipColumnOrder());
    dispatch(setWipColumnOrder(hydrated));
    writeStoredColumnOrder(hydrated);
  }, [dispatch]);

  /**
   * The picker always renders a fully reconciled list — never the raw, possibly-stale Redux
   * value. If Redux is empty (first paint before hydration completes, or after a reset) we
   * fall back to the configuration defaults so cards still appear.
   */
  const columnOrder = useMemo<WipColumnConfigI[]>(
    (): WipColumnConfigI[] =>
      reduxOrder.length > 0
        ? reconcileWipColumnOrder(reduxOrder)
        : reconcileWipColumnOrder(generateDefaultWipColumnOrder()),
    [reduxOrder]
  );

  const handleOrderChange = useCallback(
    (result: DropResult): void => {
      if (!result.destination) return;
      const { source, destination } = result;
      if (source.index === destination.index) return;
      const next = columnOrder.slice();
      const [moved] = next.splice(source.index, 1);
      next.splice(destination.index, 0, moved);
      dispatch(
        reorderWipColumn({ sourceIndex: source.index, destinationIndex: destination.index })
      );
      writeStoredColumnOrder(next);
    },
    [columnOrder, dispatch]
  );

  const handleVisibilityToggle = useCallback(
    (columnId: string): void => {
      const target = columnOrder.find((c): boolean => c.id === columnId);
      if (!target || target.required) return;
      const next = columnOrder.map(
        (c): WipColumnConfigI => (c.id === columnId ? { ...c, visible: !c.visible } : c)
      );
      dispatch(toggleWipColumnVisibility(columnId));
      writeStoredColumnOrder(next);
    },
    [columnOrder, dispatch]
  );

  const handleResetConfig = useCallback((): void => {
    const defaults = generateDefaultWipColumnOrder();
    dispatch(setWipColumnOrder(defaults));
    clearStoredColumnOrder();
  }, [dispatch]);

  const handleSaveConfig = useCallback((): void => {
    writeStoredColumnOrder(columnOrder);
  }, [columnOrder]);

  return {
    columnOrder,
    handleOrderChange,
    handleVisibilityToggle,
    handleResetConfig,
    handleSaveConfig,
  };
};
